import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ReactElement, useCallback, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import { Dialog } from '@app/components/Shared/Dialog';
import { Loading } from '@app/components/Shared/Loading';
import {
  attributesByClinicalDepartmentSelector,
  attributesByJobTypeSelector,
  categorySelector,
  searchConditionState,
} from '@app/domain/catalog';
import { Attribute } from '@app/types/catalog';
import { getSearchResultUrl } from '@app/utils/catalog';

export const tabs = [
  { label: '診療科', value: 'clinicalDepartment' },
  { label: '仕事種別', value: 'jobType' },
  { label: '給料幅', value: 'variant' },
] as const;
export const variantTypeOptions = [
  { label: '時給', value: 'hour' },
  { label: '日給', value: 'day' },
] as const;

export interface ConditionProps {
  attributes?: string[];
  max?: string;
  min?: string;
  type?: string;
}
interface AttributesModalProps {
  category?: string;
  condition?: ConditionProps;
  onClickSearch?: (conditions: ConditionProps) => void;
  onClose: () => void;
  open: boolean;
  tabValue?: string;
}

export function AttributesModal({
  category,
  condition,
  onClickSearch,
  onClose,
  open,
  tabValue,
}: AttributesModalProps): ReactElement {
  const navigate = useNavigate();
  const setConditionState = useSetRecoilState(searchConditionState);
  const categories = useRecoilValue(categorySelector);
  const attributesByClinicalDepartment = useRecoilValue(
    attributesByClinicalDepartmentSelector
  );
  const attributesByJobType = useRecoilValue(attributesByJobTypeSelector);
  const [selectedTab, setSelectedTab] = useState<string>(tabs[0].value);
  const [conditions, setConditions] = useState<ConditionProps>({
    attributes: condition?.attributes || [],
    max: condition?.max || '',
    min: condition?.min || '',
    type: condition?.type || variantTypeOptions[0].value,
  });

  const clinicalDeptItems = useMemo(() => {
    return (
      attributesByClinicalDepartment?.reduce(
        (items: Attribute[], item: Attribute) => {
          const foundGroup = items.find((i) => i.name === item.name);
          if (foundGroup) {
            foundGroup.children?.push(item);
          } else {
            items.push({
              ...item,
              children: [item],
            });
          }
          return items;
        },
        []
      ) || []
    );
  }, [attributesByClinicalDepartment]);

  const categoryId = useMemo(() => {
    return categories?.find((c) => c.name === category)?.id || '';
  }, [categories, category]);

  const isGroupChecked = (name: string) => {
    return !!clinicalDeptItems
      .find((i) => i.name === name)
      ?.children?.every((c) => conditions.attributes?.includes(c.id));
  };

  const isIndeterminate = (name: string) => {
    const children = clinicalDeptItems.find((i) => i.name === name)?.children;
    return !!(
      children?.some((c) => conditions.attributes?.includes(c.id)) &&
      !children?.every((c) => conditions.attributes?.includes(c.id))
    );
  };

  const handleChangeTab = useCallback(
    (event: React.SyntheticEvent, value: string) => {
      setSelectedTab(value);
    },
    [setSelectedTab]
  );

  const handleChangeChecked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, name?: string) => {
      const targetIds: string[] = [];
      if (name) {
        clinicalDeptItems
          .find((c) => c.name === name)
          ?.children?.forEach((c) => targetIds.push(c.id));
      } else {
        targetIds.push(event.target.value);
      }
      const data = conditions.attributes || [];
      setConditions({
        ...conditions,
        attributes: event.target.checked
          ? [...data, ...targetIds]
          : data.filter((c) => !targetIds.includes(c)),
      });
    },
    [clinicalDeptItems, conditions]
  );

  const handleClose = useCallback((): void => {
    onClose();
    setSelectedTab(tabValue || tabs[0].value);
  }, [onClose, tabValue]);

  useEffect(() => {
    setSelectedTab(tabValue || tabs[0].value);
  }, [tabValue]);

  const handleClickSearch = useCallback(() => {
    if (onClickSearch) {
      onClickSearch(conditions);
      return;
    }
    setConditionState(null);
    navigate(
      getSearchResultUrl({
        attributeIds: conditions.attributes,
        categoryId,
        variant: {
          max: conditions.max,
          min: conditions.min,
          type: conditions.type,
        },
      })
    );
    handleClose();
  }, [
    categoryId,
    conditions,
    handleClose,
    navigate,
    onClickSearch,
    setConditionState,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="詳細条件を追加・変更"
      content={
        <>
          <Tabs
            value={selectedTab}
            onChange={handleChangeTab}
            variant="fullWidth"
          >
            {tabs.map(({ label, value }, index) => (
              <Tab key={index} label={label} value={value} />
            ))}
          </Tabs>
          {selectedTab === tabs[1].value ? (
            <>
              {attributesByJobType.length < 1 ? (
                <Loading />
              ) : (
                <Box sx={{ px: 2, py: 1 }}>
                  <Grid container spacing={1}>
                    {attributesByJobType.map((item: Attribute) => (
                      <Grid item xs={6} key={item.id}>
                        <FormControlLabel
                          label={item.name}
                          control={
                            <Checkbox
                              value={item.id}
                              checked={conditions.attributes?.includes(item.id)}
                              onChange={(e) => handleChangeChecked(e)}
                            />
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </>
          ) : selectedTab === tabs[2].value ? (
            <Stack spacing={2} sx={{ px: 2, py: 1 }}>
              <Select
                value={conditions.type}
                onChange={(e) =>
                  setConditions({ ...conditions, type: e.target.value })
                }
              >
                {variantTypeOptions.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={conditions.min}
                  variant="outlined"
                  placeholder="下限金額"
                  onChange={(e) => {
                    setConditions({ ...conditions, min: e.target.value });
                  }}
                />
                <Typography>円</Typography>
                <Typography>〜</Typography>
                <TextField
                  value={conditions.max}
                  variant="outlined"
                  placeholder="上限金額"
                  onChange={(e) => {
                    setConditions({ ...conditions, max: e.target.value });
                  }}
                />
                <Typography>円</Typography>
              </Stack>
            </Stack>
          ) : (
            <Box>
              {clinicalDeptItems.length < 1 ? (
                <Loading />
              ) : (
                clinicalDeptItems.map((item: Attribute) => (
                  <Accordion
                    key={item.id}
                    TransitionProps={{ unmountOnExit: true }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" alignItems="center">
                        <Checkbox
                          checked={isGroupChecked(item.name)}
                          indeterminate={isIndeterminate(item.name)}
                          onChange={(e) => handleChangeChecked(e, item.name)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Typography>{item.name}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1}>
                        {item.children?.map((child) => (
                          <Grid item xs={6} key={child.id}>
                            <FormControlLabel
                              label={child.items[0].value}
                              control={
                                <Checkbox
                                  value={child.id}
                                  checked={conditions.attributes?.includes(
                                    child.id
                                  )}
                                  onChange={(e) => handleChangeChecked(e)}
                                />
                              }
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))
              )}
            </Box>
          )}
        </>
      }
      action={
        <Stack direction="row" spacing={2} width={1}>
          <Button
            color="inherit"
            variant="outlined"
            fullWidth
            onClick={handleClose}
          >
            キャンセル
          </Button>
          <Button
            color="primary"
            type="submit"
            variant="contained"
            fullWidth
            onClick={handleClickSearch}
          >
            検索
          </Button>
        </Stack>
      }
    />
  );
}
